import { useCallback, useMemo, useState } from "react";
import { signUp } from "../../store/action/auth";
import { Button, Input, Card, AlertModal } from "@biteship/atom";
import PhoneNumberPrefix from "./components/PhoneNumberPrefix";
import CourierType from "./components/CourierType";
import CourierCode from "./components/CourierCode";
import { normalizePhoneNumber } from "./helpers/validate";

const SignUp = () => {
  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phonePrefix: "62",
    courierType: "",
    courierCode: "", // could have value "other"
    openCourierName: "",
    openCourierCode: "",
    openCourierCodePlaceholder: "",
    password: "",
    error: "",
    channel: "",
    success: false,
    loading: false,
  };
  const [values, setValues] = useState(initialValues);
  const isOpenCourier = useMemo(() => values.courierCode === "other", [values.courierCode]);

  const { error } = values;

  const onChangeOpenCourierName = useCallback((newName) => {
    // allow empty string or alphanumeric and spaces
    if (newName !== '' && (!/^[a-zA-Z0-9\s]+$/.test(newName) || newName.length > 25)) return;
    const placeholder = newName.replaceAll(" ", "_").toLowerCase();
    setValues({ ...values, openCourierName: newName, openCourierCodePlaceholder: placeholder });
  }, [values]);

  const onChangeOpenCourierCode = useCallback((newCode) => {
    // allow empty string or lowered case alphanumeric and underscores
    if (newCode !== '' && (!/^[a-z0-9_]+$/.test(newCode) || newCode.length > 25)) return;
    setValues({ ...values, openCourierCode: newCode });
  }, [values]);

  const onSubmit = () => {
    setValues({ ...values, loading: true });
    const userPayload = {...values};
    if (isOpenCourier) {
      if (!values.openCourierName) {
        setValues({ ...values, error: "Nama kurir tidak boleh kosong" });
        return;
      }
      if (!values.openCourierCode) {
        setValues({ ...values, error: "Kode kurir tidak boleh kosong" });
        return;
      }
      userPayload.courierCode = values.openCourierCode;
      userPayload.courierName = values.openCourierName;
    }
    signUp(userPayload)
      .then(() => {
        setValues({
          ...initialValues,
          success: true,
        });
      })
      .catch((error) => {
        const errMessage = error?.response?.data?.error || "Gagal daftar akun kurir";
        console.log(errMessage);
        alert(errMessage);
        setValues({
          ...values,
          error: error?.response?.data?.error || error.message,
          loading: false,
        });
      });
  };

  const showError = () => (
    <div className="alert alert-danger rounded" style={{ display: error ? "" : "none" }}>
      {error}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <AlertModal
        type="success"
        open={values.success}
        title={"Akun Anda Berhasil Terdaftar!"}
        description={
          "Silahkan lanjut ke halaman login dengan menggunakan email yang sudah Anda daftarkan"
        }
        buttonTitle={"Ke Halaman Login"}
        setOpen={() => window.location.assign(`/`)}
        onClick={() => window.location.assign(`/`)}
      />
      <div className="sm:mx-auto sm:w-full sm:max-w-xl">
        <div className="flex justify-center">
          <img className="rounded" style={{ height: 72 }} src="/images/logos/biteship.png" alt="" />
        </div>
        <br />
        {showError()}
        <Card className="space-y-6 p-16" shadow>
          <div className="text-gray-900 font-bold text-2xl">Daftar akun baru Anda</div>
          <div className="space-y-6">
            <Input
              label="Nama Depan"
              name="firstName"
              className="sm:col-span-3 text-gray-900"
              onChange={(e) => setValues({ ...values, firstName: e.target.value })}
              placeholder="John"
              value={values.firstName}
            />
            <Input
              label="Nama Belakang"
              name="lastName"
              className="sm:col-span-3 text-gray-900"
              onChange={(e) => setValues({ ...values, lastName: e.target.value })}
              placeholder="Doe"
              value={values.lastName}
            />
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-900">
                Nomor Handphone
              </label>
              <div className="flex space-x-2 mb-4">
                <PhoneNumberPrefix
                  onSelectPrefix={(value) => setValues({ ...values, phonePrefix: value })}
                />
                <Input
                  onChange={(e) =>
                    setValues({ ...values, phone: normalizePhoneNumber(e.target.value) })
                  }
                  placeholder="81212345678"
                  className="sm:col-span-3 w-full text-gray-900"
                  value={values.phone}
                  labelHidden
                />
              </div>
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-900">Nama Kurir</label>
              <CourierCode 
                onSelect={(value) => {
                  setValues({ ...values, courierCode: value });
                }} 
              />
            </div>
            {isOpenCourier && (
              <>
                <Input
                  label="Nama Kurir Lainnya"
                  name="courierName"
                  className="sm:col-span-3 text-gray-900"
                  onChange={(e) => onChangeOpenCourierName(e.target.value)}
                  placeholder="Nama Kurir Anda"
                  value={values.openCourierName}
                />
                <div>
                  <Input
                    label="Kode Kurir Lainnya"
                    name="courierCode"
                    className="sm:col-span-3 text-gray-900"
                    onChange={(e) => onChangeOpenCourierCode(e.target.value)}
                    placeholder={"Kode Kurir Anda"}
                    value={values.openCourierCode}
                  />
                  {values.openCourierCodePlaceholder && (
                    <p className="text-xs text-gray-700 mb-0 mt-1">
                      {`Contoh: ${values.openCourierCodePlaceholder}`}
                    </p>
                  )}
                </div>
              </>
            )}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-900">Tipe Kurir</label>
              <CourierType onSelect={(value) => setValues({ ...values, courierType: value })} />
            </div>
            <Input
              label="Email"
              name="email"
              className="sm:col-span-3 text-gray-900"
              onChange={(e) => setValues({ ...values, email: e.target.value })}
              placeholder="Email@anda.com"
              value={values.email}
            />
            <Input
              label="Password"
              name="password"
              className="sm:col-span-3 text-gray-900"
              onChange={(e) => setValues({ ...values, password: e.target.value })}
              type="password"
              placeholder="Password Anda"
              value={values.password}
            />
            <Button
              className="w-full justify-center"
              loading={values.loading}
              title="Daftar"
              onClick={onSubmit}
            />
          </div>
        </Card>
        <div className="mt-4 text-gray-700">
          Sudah punya akun?{" "}
          <text
            className="font-bold text-primary cursor-pointer"
            onClick={() => window.location.assign("/signin")}
          >
            Masuk disini
          </text>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
